import { PublishedFormId } from '@furnish-hx/constants/FormId';
import FormTypeId from '@core/constants/FormTypeId';
import { LibraryShapeId, OpeningShapeId, WardrobeShapeId } from '@core/constants/Shape';
import { FieldDimensionId } from '@core/constants/FieldDimension';
import { MaterialId } from '@core/constants/Material';
import fieldPortfolioData from './common/fieldsPortfolioData';
import {
    IFormFieldKey,
    IForm,
    IFormInputType,
    IDimensionUnit,
    IFormFieldDimensionDefaultValueKey,
} from '../../interfaces/Form';
import {
    allGlasses, allMetals, allWoods, allMelamine, getMaterialsWithExceptions,
} from './common/materialGroups';
import fieldsContact from './common/fieldsContact';
import additionalDataFactory from './common/fieldsAdditionalData';

const storageUnderStairs: IForm = {
    formId       : PublishedFormId.StorageUnderStairs,
    type         : FormTypeId.Wardrobe,
    thumbnailURL : '/resized-images/forms/storageUnderStairs.jpg',
    fieldsOrder  : [
        IFormFieldKey.PortfolioData,
        IFormFieldKey.Comment,
        IFormFieldKey.Material,
        IFormFieldKey.Dimension,
        IFormFieldKey.Openings,
        IFormFieldKey.AddOns,
        IFormFieldKey.AdditionalData,
        IFormFieldKey.Contact,
    ],
    fields: {
        portfolioData : fieldPortfolioData,
        comment       : {
            comment: {
                value    : undefined,
                required : false,
            },
        },
        material: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions([
                    ...allWoods,
                    ...allMelamine,
                    ...allGlasses,
                    ...allMetals,
                ],
                [
                    MaterialId.BlackLocust,
                    MaterialId.Iroko,
                    MaterialId.Douglas,
                    MaterialId.DouglasEngineered,
                    MaterialId.DouglasRecycled,
                    MaterialId.DouglasVeneer,
                    MaterialId.Redcedar,
                    MaterialId.RedcedarEngineered,
                    MaterialId.RedcedarRecycled,
                    MaterialId.RedcedarVeneer,
                    MaterialId.Larch,
                    MaterialId.LarchEngineered,
                    MaterialId.LarchRecycled,
                    MaterialId.LarchVeneer,
                    MaterialId.Padouk,
                    MaterialId.PadoukEngineered,
                    MaterialId.PadoukRecycled,
                    MaterialId.PadoukVeneer,
                    MaterialId.Ipe,
                    MaterialId.IpeEngineered,
                    MaterialId.IpeRecycled,
                    MaterialId.IpeVeneer,
                    MaterialId.Poplar,
                    MaterialId.PoplarRecycled,
                    MaterialId.PoplarVeneer,
                    MaterialId.PoplarEngineered,
                    MaterialId.DriftWood,
                    MaterialId.DriftWoodRecycled,
                    MaterialId.Garapa,
                    MaterialId.SheetMetal,
                ]),
                required: true,
            },
        },
        dimension: {
            [FieldDimensionId.Height]: {
                value        : undefined,
                type         : IFormInputType.Range,
                unit         : IDimensionUnit.Centimeters,
                step         : 100,
                range        : { min: 1500, max: 3000 },
                required     : true,
                defaultValue : 1800,
            },
            [FieldDimensionId.Length]: {
                value        : undefined,
                type         : IFormInputType.Range,
                unit         : IDimensionUnit.Centimeters,
                step         : 100,
                range        : { min: 500, max: 3000 },
                required     : true,
                defaultValue : 1500,
            },
            [FieldDimensionId.Depth]: {
                value        : undefined,
                type         : IFormInputType.Range,
                unit         : IDimensionUnit.Centimeters,
                step         : 10,
                range        : { min: 300, max: 1000 },
                required     : true,
                defaultValue : 600,
            },
            [FieldDimensionId.Thickness]: {
                value         : undefined,
                type          : IFormInputType.Range,
                unit          : IDimensionUnit.Centimeters,
                step          : 10,
                range         : { min: 10, max: 50 },
                required      : true,
                defaultValue  : 20,
                defaultValues : [
                    [IFormFieldDimensionDefaultValueKey.StandardThickness, 20],
                ],
            },
        },
        openings: {
            [OpeningShapeId.DoubleDoor]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [OpeningShapeId.SingleDoor]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [OpeningShapeId.SlidingDoors]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
        },
        addOns: {
            [WardrobeShapeId.BarHangerCloth]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [WardrobeShapeId.BeltHanger]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [WardrobeShapeId.DrawerCase]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [LibraryShapeId.Niche]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [WardrobeShapeId.PantsHanger]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [LibraryShapeId.Shelf]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [WardrobeShapeId.ShoesDrawer]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [WardrobeShapeId.TelescopicHangerCloth]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [WardrobeShapeId.TieHanger]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [WardrobeShapeId.WardrobeLight]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
        },
        additionalData: additionalDataFactory({
            budgetMin          : 1000,
            budgetMax          : 8000,
            budgetDefaultValue : 2500,
        }),
        contact: fieldsContact,
    },
};

export default storageUnderStairs;
